import React from 'react';
import PropTypes from 'prop-types';
import {shallowEqual, useSelector} from 'react-redux';
import {actionIcRestore24px} from '@ey/motif-react-wrapper/assets/icons/index';
import styled from 'styled-components';
import Icon from '../../BaseComponentWrappers/MotifIcon';
import {getAccountById, getAssertionById, getRiskById} from '../../../../selectors/Ares/selectors';
import {getAccountType, getRiskTypeIndicator} from './AccountSummaryConfig';
import {riskTypes} from '../../../../util/uiconstants';
import {isNullOrUndefined, labels} from '../../../../util/utils';
import EllipsesControl from '../../EllipsesControl/EllipsesControl';
import Tooltip from '../../BaseComponentWrappers/MotifTooltip';

export default function AccountName({accountId}) {
	const {accountName, accountTypeId, assertions, hasEstimate, priorAccountTypeID, priorHasEstimate} =
		useSelector((state) => getAccountById(state, accountId), shallowEqual) || {};

	const risks = useSelector((state) =>
		assertions
			?.map((assertionId) => getAssertionById(state, assertionId))
			.flatMap((assertion) => assertion && assertion.risks?.map((riskId) => getRiskById(state, riskId)))
			.filter((value, index, self) => self.findIndex((risk) => risk && risk.id === value.id) === index)
	);

	const hasFraudRiskAssociated = risks && risks.some((risk) => risk.riskTypeId === riskTypes.FRAUDRISK),
		hasSignificantRiskAssociated = risks && risks.some((risk) => risk.riskTypeId === riskTypes.SIGNIFICANTRISK),
		hasInherentRiskAssociated = risks && risks.some((risk) => risk.riskTypeId === riskTypes.INHERENTRISK);

	return (
		<StyledSection className="accountname-cellrenderer">
			<section className="text-renderer">
				<EllipsesControl
					noOfLines={1}
					content={accountName}
					tooltip={accountName}
					tooltipClass="tooltipCustomWidth"
				/>
			</section>
			{'        '}
			<>
				{/*******This change is a part of story id #974592 *******/}
				{displayPriorPeriodChangeIcon(accountTypeId, hasEstimate, priorAccountTypeID, priorHasEstimate)}
			</>
			{'        '}
			<>
				{hasFraudRiskAssociated || hasSignificantRiskAssociated || hasInherentRiskAssociated ? (
					<section className="risktype-badge-wrapper">
						<Tooltip
							placement="top"
							className={'risktypebadge-tooltip'}
							variant="alt"
							trigger={
								<span className="risktype-badge">
									{getRiskTypeIndicator(
										hasFraudRiskAssociated,
										hasSignificantRiskAssociated,
										hasInherentRiskAssociated
									)}
								</span>
							}
						>
							{getRiskToolTipData(
								risks.filter((risk) => risk.riskTypeId === riskTypes.FRAUDRISK),
								risks.filter((risk) => risk.riskTypeId === riskTypes.SIGNIFICANTRISK),
								risks.filter((risk) => risk.riskTypeId === riskTypes.INHERENTRISK)
							)}
						</Tooltip>
					</section>
				) : null}
			</>
			{'        '}
		</StyledSection>
	);
}

const getRiskToolTipData = (fraudRisk, significantRisk, inherentRisk) => (
	<React.Fragment>
		{significantRisk.length > 0 && (
			<section className="risktypebadge-tooltipcontent">
				<h3>{labels.significantRisk}</h3>
				<ul>
					{significantRisk.map((risk) => {
						return <li className="tooltipCustomWidth">{risk.riskName}</li>;
					})}
				</ul>
			</section>
		)}
		{fraudRisk.length > 0 && (
			<section className="risktypebadge-tooltipcontent">
				<h3>{labels.fraudRisk}</h3>
				<ul>
					{fraudRisk.map((risk) => {
						return <li className="tooltipCustomWidth">{risk.riskName}</li>;
					})}
				</ul>
			</section>
		)}
		{inherentRisk.length > 0 && (
			<section className="risktypebadge-tooltipcontent">
				<h3>{labels.inherentRisk}</h3>
				<ul>
					{inherentRisk.map((risk) => {
						return <li className="tooltipCustomWidth">{risk.riskName}</li>;
					})}
				</ul>
			</section>
		)}
	</React.Fragment>
);

const displayPriorPeriodChangeIcon = (accountTypeId, hasEstimate, priorAccountTypeId, priorHasEstimate) => {
	if (
		(!isNullOrUndefined(accountTypeId) &&
			!isNullOrUndefined(priorAccountTypeId) &&
			accountTypeId !== priorAccountTypeId) ||
		(!isNullOrUndefined(hasEstimate) && !isNullOrUndefined(priorHasEstimate) && hasEstimate !== priorHasEstimate)
	) {
		return (
			<section className="priorprdchanges-wrapper">
				<Tooltip
					placement="top"
					closeButtonProps={{
						className: 'hide'
					}}
					className={'priorprdchanges-tooltip'}
					variant="alt"
					trigger={
						<span className="priorperiod-changes-icon">
							{' '}
							<Icon src={actionIcRestore24px} />
						</span>
					}
				>
					{getPriorDesignation(accountTypeId, priorAccountTypeId)}
					{getPriorEstimation(hasEstimate, priorHasEstimate)}
				</Tooltip>
			</section>
		);
	}
	return null;
};

////This change is a part of story id #974592
const getPriorDesignation = (accountTypeId, priorAccountTypeId) => {
	if (
		!isNullOrUndefined(accountTypeId) &&
		!isNullOrUndefined(priorAccountTypeId) &&
		accountTypeId !== priorAccountTypeId
	)
		return (
			<section>
				<span className="priorprdtooltip-label">{labels.priorYearDesignation}</span>
				<span className="priorprdtooltip-value">{': ' + getAccountType(priorAccountTypeId)}</span>
			</section>
		);
	else return null;
};

////This change is a part of story id #974592
const getPriorEstimation = (hasEstimate, priorHasEstimate) => {
	if (!isNullOrUndefined(hasEstimate) && !isNullOrUndefined(priorHasEstimate) && hasEstimate !== priorHasEstimate)
		return (
			<section>
				<span className="priorprdtooltip-label">{labels.priorYearEstimation}</span>
				<span className="priorprdtooltip-value">{priorHasEstimate ? ': ' + labels.yes : ': ' + labels.no}</span>
			</section>
		);
	else return null;
};

AccountName.propTypes = {
	className: PropTypes.string,
	accountId: PropTypes.number.isRequired
};

AccountName.defaultProps = {
	className: 'accountname-cellrenderer'
};

const StyledSection = styled.section`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
	height: 100%;
	padding-right: var(--px-5);
	font-weight: 600;
	font-size: var(--px-16);
	line-height: var(--px-24);
	color: var(--neutrals-700);
	.text-renderer {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		margin-right: var(--px-15);
		max-width: calc(100% - var(--px-130));
		width: auto;
	}
	.priorprdchanges-wrapper {
		width: var(--px-25);
		display: flex;
		justify-content: flex-start;
		margin-right: var(--px-5);
		.priorperiod-changes-icon {
			width: var(--px-18) !important;
			height: var(--px-18) !important;
			svg {
				width: var(--px-18) !important;
				height: var(--px-18) !important;
			}
		}
		.motif-tooltip-wrapper {
			height: var(--px-18);
		}
	}
	.risktype-badge-wrapper {
		width: auto;
		max-width: var(--px-100);
		display: flex;
		justify-content: flex-start;
		.motif-tooltip-wrapper {
			width: 100%;
			.motif-tooltip-trigger-wrap {
				width: 100%;
			}
		}
		.risktype-badge {
			border-radius: var(--px-4);
			padding: var(--px-4) var(--px-5);
			background: var(--neutrals-900);
			color: var(--neutrals-00white);
			font-style: italic;
			font-weight: 700;
			font-size: var(--px-10);
			line-height: var(--px-10);
			cursor: pointer;
			letter-spacing: var(--px-1);
			.ellipses {
				justify-content: center;
			}
		}
	}
`;
